import { Box, Button, Flex, HStack, Icon, IconButton, SimpleGrid, Spinner, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus, FaTrash } from "react-icons/fa";
import {
  ContributingEntityRole,
  ContributingEntityStatus,
  type ContributingEntityDto,
  type EspdResponseDto,
  type SelectionCriterionDto,
  useListContributingEntitiesQuery,
  useRemoveContributingEntityMutation,
  useResendContributingEntityInvitationMutation,
} from "../../../../../autogen/bff-api";
import { LabeledValue } from "../../../../../common/components/LabeledValue";
import { espdAnswerFor, removeEspdAnswer, setEspdAnswer } from "../espd/useEspdAnswers";
import { groupRoleLabel } from "./contributingEntityRoles";
import { GroupDetailsFields } from "./GroupDetailsFields";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useReportQueryError } from "../../../../../common/errors/useReportQueryError";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { AddContributingEntityModal } from "./AddContributingEntityModal";
import { EditContributingEntityModal } from "./EditContributingEntityModal";
import { ContributingEntityStatusTag, hasSubmittedAnswer } from "./ContributingEntityStatusTag";
import { useContributionUrls } from "../../contributing/useContributionUrls";

interface Props {
  bidId: string;
  role: ContributingEntityRole;
  selectionCriteria: SelectionCriterionDto[];
  readOnly: boolean;
  espd: EspdResponseDto;
  onEspdChange: (next: EspdResponseDto) => void;
  groupRoleRequirementId?: string;
  groupNameRequirementId?: string;
}

export const ContributingEntitiesPanel = ({
  bidId,
  role,
  selectionCriteria,
  readOnly,
  espd,
  onEspdChange,
  groupRoleRequirementId,
  groupNameRequirementId,
}: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const confirmation = useConfirmationModal();
  const contributionUrls = useContributionUrls();
  const [isAdding, setIsAdding] = useState(false);
  const [editing, setEditing] = useState<ContributingEntityDto | undefined>();
  const [resending, setResending] = useState<string | undefined>();

  const { data, isLoading, error } = useListContributingEntitiesQuery({ bidId });
  const [removeContributingEntity] = useRemoveContributingEntityMutation();
  const [resendInvitation] = useResendContributingEntityInvitationMutation();

  useReportQueryError(error);

  const entities = (data?.items ?? []).filter((e) => e.role === role);

  const groupName =
    (groupNameRequirementId && espdAnswerFor(espd, groupNameRequirementId)?.description) || "";
  const leadRole =
    (groupRoleRequirementId && espdAnswerFor(espd, groupRoleRequirementId)?.description) || "";

  const declarationNote =
    role === ContributingEntityRole.ConsortiumMember
      ? t("Listed in the self-declaration's Part II A b).")
      : role === ContributingEntityRole.Subcontractor
        ? t("Listed in the self-declaration's Part II D.")
        : undefined;

  const remove = (entityId: string, name: string) =>
    confirmation.show({
      title: t("Remove company"),
      description: t(
        "{{name}} is removed from the offer and its answers are no longer counted.",
        { name },
      ),
      confirmButtonText: t("Remove") ?? undefined,
      onConfirmed: async () => {
        try {
          await removeContributingEntity({ bidId, entityId }).unwrap();
        } catch (e) {
          displayer.trigger(e);
        }
      },
    });

  const resend = async (entityId: string) => {
    setResending(entityId);
    try {
      await resendInvitation({ bidId, entityId }).unwrap();
    } catch (e) {
      displayer.trigger(e);
    } finally {
      setResending(undefined);
    }
  };

  return (
    <Box mt="5" borderTopWidth="1px" borderColor="smBorder" pt="5">
      {groupRoleRequirementId && (
        <GroupDetailsFields
          groupName={groupName}
          leadRole={leadRole}
          showNameField={groupNameRequirementId !== undefined}
          readOnly={readOnly}
          onGroupNameChange={(value) =>
            onEspdChange(
              value.trim()
                ? setEspdAnswer(espd, groupNameRequirementId!, { description: value })
                : removeEspdAnswer(espd, groupNameRequirementId!),
            )
          }
          onLeadRoleChange={(value) =>
            onEspdChange(setEspdAnswer(espd, groupRoleRequirementId, { description: value }))
          }
        />
      )}

      <Flex align="center" justify="space-between" mb="3">
        <Text fontSize="md" fontWeight="semibold">
          {t(role)}
        </Text>
        {isLoading && <Spinner size="sm" color="smMuted" />}
      </Flex>

      {entities.length === 0 && !isLoading && (
        <Text fontSize="sm" color="smMuted" mb="3">
          {t("Add at least one company. Once one is added you can deliver, even if it has not answered yet.")}
        </Text>
      )}

      {entities.length > 0 && (
        <Stack spacing="3" mb="3">
          {entities.map((entity) => (
            <Box key={entity.id} borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4">
              <Flex align="center" justify="space-between" gap="3" mb="3">
                <Text fontWeight="semibold">{entity.name}</Text>
                <HStack spacing="2">
                  <ContributingEntityStatusTag
                    status={entity.status}
                    to={
                      hasSubmittedAnswer(entity.status)
                        ? contributionUrls.review(bidId, entity.id)
                        : undefined
                    }
                  />
                  {!readOnly && !hasSubmittedAnswer(entity.status) && (
                    <IconButton
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      aria-label={t("Remove company") ?? ""}
                      icon={<Icon as={FaTrash} />}
                      onClick={() => remove(entity.id, entity.name)}
                    />
                  )}
                </HStack>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: entity.groupRole ? 3 : 2 }} spacingX="8" spacingY="3">
                <LabeledValue label={t("Organization number")} value={entity.organizationNumber} />
                <LabeledValue label={t("Contact email")} value={entity.contactEmail} />
                {entity.groupRole && (
                  <LabeledValue label={groupRoleLabel(entity.role, t)} value={entity.groupRole} />
                )}
              </SimpleGrid>

              {!readOnly && !hasSubmittedAnswer(entity.status) && (
                <HStack spacing="1" mt="3">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(entity)}>
                    {t("Edit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    isLoading={resending === entity.id}
                    onClick={() => resend(entity.id)}
                  >
                    {entity.status === ContributingEntityStatus.InvitationDeclined
                      ? t("Invite again")
                      : t("Send reminder")}
                  </Button>
                </HStack>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {!readOnly && (
        <Button
          size="sm"
          variant="link"
          colorScheme="teal"
          leftIcon={<Icon as={FaPlus} />}
          onClick={() => setIsAdding(true)}
        >
          {t("Add participating company")}
        </Button>
      )}

      {declarationNote && (
        <Text fontSize="xs" color="smMuted" mt="3">
          {declarationNote}
        </Text>
      )}

      {isAdding && (
        <AddContributingEntityModal
          bidId={bidId}
          role={role}
          selectionCriteria={selectionCriteria}
          onClose={() => setIsAdding(false)}
        />
      )}
      {editing && (
        <EditContributingEntityModal
          bidId={bidId}
          entity={editing}
          selectionCriteria={selectionCriteria}
          onClose={() => setEditing(undefined)}
        />
      )}
      {confirmation.modal}
    </Box>
  );
};
